/**
 * OnboardingWalkthrough.
 *
 * Mounts in AppLayout. Two-mode UI driven by the user's
 * OnboardingProgress state:
 *
 *   1. Welcome modal - shown once, on the very first visit
 *      (`completed_steps` is empty AND not dismissed). Big centered
 *      modal: "Welcome to TNN. We'll walk you through 5 things."
 *      Clicking "Show me around" marks step "welcome" complete and
 *      transitions to mode 2.
 *
 *   2. Persistent panel - fixed top-right, collapsible. Shows the
 *      remaining steps with a progress bar. Each step has a CTA that
 *      navigates to the relevant page; once the user takes the action
 *      they were guided toward, the BE marks the step complete (via
 *      auto-detection - see "auto-completion" below).
 *
 * The panel disappears entirely when:
 *   - `is_dismissed` is true (user clicked Skip)
 *   - `is_complete` is true (all 5 steps done)
 *
 * Auto-completion:
 * We don't make the user click "I did the thing." Instead, the relevant
 * pages/components signal completion via the `useOnboardingMark()` hook:
 *   - CommunityPage / TeamDirectoryPage mount → mark "people"
 *   - Creating an Interaction succeeds → mark "interaction"
 *   - CopilotDrawer opens → mark "copilot"
 *   - PlaybookLauncher launches a playbook → mark "playbook"
 *
 * Each call is idempotent server-side; spam-clicks don't double-count.
 */
import { useState } from "react";

import { Check, ChevronDown, ChevronUp, X } from "lucide-react";
import { Link } from "react-router-dom";

import { cn } from "@/shared/lib/cn";
import { getHelpUrl } from "@/shared/lib/getHelpUrl";
import { Button } from "@/shared/ui/Button";

import {
  useDismissOnboardingMutation,
  useGetOnboardingProgressQuery,
  useMarkOnboardingStepMutation,
} from "../api/onboardingApi";
import { STEP_CONTENT, type OnboardingStepKey } from "../model/types";


/** Onboarding walkthrough UI, driven by the user's OnboardingProgress. */
export function OnboardingWalkthrough() {
  const { data: progress } = useGetOnboardingProgressQuery();
  const [markStep] = useMarkOnboardingStepMutation();
  const [dismiss] = useDismissOnboardingMutation();
  // Local state: panelCollapsed
  const [panelCollapsed, setPanelCollapsed] = useState(false);

  if (!progress) return null;
  if (progress.is_dismissed) return null;
  if (progress.is_complete) return null;

  const completedSet = new Set(progress.completed_steps);
  const remainingSteps = progress.all_steps.filter((s) => !completedSet.has(s));
  const isFirstVisit = progress.completed_steps.length === 0;

  // Welcome modal - first visit only. Marking "welcome" advances to
  // the persistent panel.
  if (isFirstVisit) {
    return (
      <WelcomeModal
        onStart={() => markStep({ step_key: "welcome" })}
        onSkip={() => dismiss()}
      />
    );
  }

  return (
    <div
      className={cn(
        "fixed right-5 top-5 z-30 w-[320px] rounded-xl border border-border bg-background shadow-lg",
        "max-w-[calc(100vw-2.5rem)]",
      )}
    >
      <header className="flex items-center justify-between border-b border-border px-4 py-2.5">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setPanelCollapsed(!panelCollapsed)}
            className="text-muted-foreground hover:text-foreground"
            aria-label={panelCollapsed ? "Expand walkthrough" : "Collapse walkthrough"}
          >
            {panelCollapsed ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
          </button>
          <div>
            <p className="text-xs font-semibold text-foreground">
              Getting started
            </p>
            <p className="text-3xs text-muted-foreground">
              {progress.completed_steps.length} of {progress.all_steps.length} steps done
            </p>
          </div>
        </div>
        <button
          onClick={() => dismiss()}
          className="text-muted-foreground hover:text-foreground"
          aria-label="Dismiss walkthrough"
        >
          <X size={14} />
        </button>
      </header>

      {!panelCollapsed && (
        <div className="px-4 py-3">
          {/* Progress bar */}
          <div className="mb-3 h-1 w-full overflow-hidden rounded-full bg-muted">
            <div
              className="h-full bg-primary transition-all"
              style={{
                width: `${(progress.completed_steps.length / progress.all_steps.length) * 100}%`,
              }}
            />
          </div>

          <div className="space-y-2">
            {progress.all_steps.map((stepKey) => (
              <StepRow
                key={stepKey}
                stepKey={stepKey}
                completed={completedSet.has(stepKey)}
                isNext={remainingSteps[0] === stepKey}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Welcome modal
// -----------------------------------------------------------------------------

function WelcomeModal({
  onStart,
  onSkip,
}: {
  onStart: () => void;
  onSkip: () => void;
}) {
  const welcome = STEP_CONTENT.welcome;
  /** Dispatches the custom event the CopilotDrawer listens for. */
  const openCopilot = () => {
    window.dispatchEvent(new CustomEvent("tnn:open-copilot"));
    // Close the modal so the drawer isn't covered. The drawer marks
    // the "copilot" onboarding step on open, so progress advances.
    onSkip();
  };
  return (
    <div className="tnn-overlay fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-md rounded-xl bg-background p-6 shadow-2xl">
        <h2 className="text-xl font-semibold text-foreground">{welcome.title}</h2>
        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
          {welcome.body}
        </p>
        {/* Three-tier escalation per BLIND_SPOTS_ANALYSIS + the user's
            note on the welcome-modal screenshot: start the walkthrough,
            ask the AI Copilot for in-app self-help, OR schedule with
            Andy. Copilot first so the cheap option is tried before the
            human one. */}
        <div className="mt-4 space-y-2 rounded-md border border-primary/20 bg-primary/5 px-3 py-2.5">
          <p className="text-xs font-medium text-foreground">
            Need help getting oriented?
          </p>
          <button
            type="button"
            onClick={openCopilot}
            className="block text-xs text-primary underline hover:text-primary/80"
          >
            Ask the AI Copilot →
          </button>
          <a
            href={getHelpUrl()}
            target="_blank"
            rel="noreferrer noopener"
            className="block text-xs text-primary underline hover:text-primary/80"
          >
            Schedule a 30-min call with Andy →
          </a>
        </div>
        <div className="mt-6 flex gap-2">
          <Button variant="default" onClick={onSkip} className="flex-1">
            Skip for now
          </Button>
          <Button variant="primary" onClick={onStart} className="flex-1">
            {welcome.cta}
          </Button>
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Step row (persistent panel)
// -----------------------------------------------------------------------------

function StepRow({
  stepKey,
  completed,
  isNext,
}: {
  stepKey: OnboardingStepKey;
  completed: boolean;
  isNext: boolean;
}) {
  const def = STEP_CONTENT[stepKey];
  if (stepKey === "welcome") {
    // The welcome step is shown only as a checkmark - never as an actionable row
    return (
      <CompletedRow title={def.title} />
    );
  }
  if (completed) {
    return <CompletedRow title={def.title} doneMessage={def.done_message} />;
  }
  return (
    <div
      className={cn(
        "rounded-lg border p-3",
        isNext
          ? "border-primary/40 bg-primary/5"
          : "border-border bg-background",
      )}
    >
      <p className="text-xs font-medium text-foreground">{def.title}</p>
      <p className="mt-1 text-2xs leading-relaxed text-muted-foreground">
        {def.body}
      </p>
      {isNext && (
        <CTA route={def.cta_route} label={def.cta} stepKey={stepKey} />
      )}
    </div>
  );
}

function CompletedRow({ title, doneMessage }: { title: string; doneMessage?: string }) {
  return (
    <div className="flex items-start gap-2 rounded-lg border border-success/30 bg-success/5 px-3 py-2">
      <Check size={14} className="mt-0.5 flex-shrink-0 text-success" />
      <div className="flex-1">
        <p className="text-xs font-medium text-foreground">{title}</p>
        {doneMessage && (
          <p className="text-3xs text-muted-foreground">{doneMessage}</p>
        )}
      </div>
    </div>
  );
}

function CTA({
  route,
  label,
  stepKey: _stepKey,
}: {
  route?: string;
  label: string;
  stepKey: OnboardingStepKey;
}) {
  // Routes get a Link; non-route steps (copilot/playbook) just describe
  // what to do - there's no explicit page to send the user to, the action
  // is in-place.
  if (route) {
    return (
      <Link
        to={route}
        className="mt-2 inline-flex rounded-md bg-primary px-2.5 py-1 text-2xs font-medium text-primary-foreground hover:bg-primary/90"
      >
        {label} →
      </Link>
    );
  }
  return (
    <p className="mt-2 text-3xs italic text-muted-foreground">
      {label} (look for the chat bubble bottom-right)
    </p>
  );
}
